import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TableHeader } from "@services/ui-components";
import { Button, Col, Row } from "react-bootstrap";
import { useReactiveVar } from "@apollo/client";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { warehouseModuleDetailsData } from "./constants";
import ToastComponent from "@me-team/common/src/components/ui-components/ToastComponent/ToastComponent";
import { CompanyAccess, ModuleInputType, ModuleProxy } from "@me-team/host/main/graphql/types";
import { ModulesTagName } from "@me-team/common/src/utils/enums";
import { useGetModulesListForTariffQuery } from "@me-team/host/main/Modules/graphql/modules.hooks";
import { useUpdateModulesWithRefetch } from "@me-team/common/src/hooks/useUpdateModuleRelation";
import Warehouse from "@me-team/common/src/images/branches/warehouse-branch.png";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";
import StartWarehouseTutorialModal from "@me-team/common/src/components/Joyride/WarehouseTutorial/Modals/StartWarehouseTutorialModal";
import { useTutorialAccess } from "@me-team/common/src/components/Joyride/useTutorialAccess";

const WarehousesModuleDetailsPage: React.FC = () => {
    const { branchId } = useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const canManageModules = useRoleAccess(CompanyAccess.TariffModulesManage);
    const canViewWarehouseList = useRoleAccess(CompanyAccess.WarehouseListView);
    const { canStartWarehouseTutorial } = useTutorialAccess();
    const [showStartWarehouseTutorialModal, setShowStartWarehouseTutorialModal] = useState(false);

    const { data, loading, refetch } = useGetModulesListForTariffQuery({
        fetchPolicy: "network-only",
        variables: { companyId: Number(currentCompanyId) },
    });

    const modules: ModuleProxy[] = data?.user?.company?.modules;
    const WAREHOUSES_MODULE: ModuleProxy = modules?.find(
        (module: ModuleProxy) => module.name === ModulesTagName.WAREHOUSES
    );

    const [updateModules, showToast, setShowToast] = useUpdateModulesWithRefetch(refetch);
    const onToggleWarehouseHandler = async () => {
        if (!canManageModules) return;
        const updates: ModuleInputType[] = [{ id: WAREHOUSES_MODULE.id, isActive: !WAREHOUSES_MODULE.active }];
        await updateModules(Number(currentCompanyId), updates, Number(branchId));
    };

    if (loading) return null;

    return (
        <div className="mb-4">
            <TableHeader
                title={t("Warehouse accounting")}
                paths={[
                    {
                        path: location?.pathname,
                        label: t("Management of Tariffs"),
                    },
                    { path: `/payment/company/${currentCompanyId}/modules/branch/${branchId}`, label: t("Modules") },
                    {
                        path: location?.pathname,
                        label: t("Warehouse accounting"),
                    },
                ]}
            ></TableHeader>

            <div className="mt-5">
                <Row>
                    <Col xl={8} xxl className="order-2 order-xl-1 flex-grow-xxl-1">
                        {warehouseModuleDetailsData.map((warehouseItem, index) => (
                            <div
                                key={index}
                                className={`module-details_wrapper d-flex justify-content-center border rounded-24 p-4 ${warehouseItem.bgClass} overflow-hidden mb-8`}
                            >
                                <Row
                                    className={`align-items-center ${warehouseItem.reverse ? "flex-row-reverse" : ""}`}
                                >
                                    <Col lg={6} xxl={5}>
                                        <h3 className="fs-24 text-capitalize mb-3">{t(warehouseItem.title)}</h3>
                                        <ul className="custom-list ps-4 mb-lg-0">
                                            {warehouseItem.items.map((item, i) => (
                                                <li key={i} className="text-grey">
                                                    {t(item.text)}
                                                    {item.subItems?.length > 0 && (
                                                        <ul className="custom-list">
                                                            {item.subItems.map((subItem, subIdx) => (
                                                                <li key={subIdx}>{t(subItem)}</li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    </Col>

                                    <Col lg={6} xxl={7} className="d-flex justify-content-center">
                                        <img
                                            className="img-fluid border rounded-12"
                                            src={warehouseItem.image}
                                            width={519}
                                            height={300}
                                            alt={t(warehouseItem.title)}
                                        />
                                    </Col>
                                </Row>
                            </div>
                        ))}
                    </Col>

                    <Col xl={4} className="order-1 order-xl-2 mb-8 mb-lg-5 mb-xl-0 module-details_column">
                        <div className="bg-violet rounded-24 text-center p-4 pt-8">
                            <div className="d-flex justify-content-center mb-4">
                                <img src={Warehouse} width={74} height={54} alt={t("Warehouse accounting")} />
                            </div>
                            <h4 className="fs-5 fw-bold mb-2 lh-lg">{t("Warehouse accounting")}</h4>
                            <p className="fs-7 text-grey mb-0 px-2">
                                {t(
                                    'The "Warehouse accounting" module allows you to fully control the movement of goods in your company — from receipt to sale and write-off.'
                                )}
                            </p>
                            <div className="d-flex align-items-center justify-content-center gap-2 my-4 py-2 flex-wrap">
                                <span className="fs-5 fw-bold">{t("Free")}</span>
                                {WAREHOUSES_MODULE.active && (
                                    <span className="badge bg-additional-green text-green rounded-1 d-inline-flex align-items-center gap-1 px-2 py-1 fs-7 fw-normal">
                                        <i className="bi bi-check2-circle" />
                                        {t("Connected")}
                                    </span>
                                )}
                            </div>
                            {!WAREHOUSES_MODULE.active ? (
                                <Button
                                    variant="primary"
                                    className="fw-bold text-capitalize w-100"
                                    disabled={!canManageModules}
                                    onClick={onToggleWarehouseHandler}
                                >
                                    {t("Enable module")}
                                </Button>
                            ) : (
                                <>
                                    <Button
                                        variant="primary"
                                        className={`fw-bold w-100 d-flex align-items-center justify-content-center mb-3
                                       ${!canViewWarehouseList ? "disabled" : ""}`}
                                        aria-disabled={!canViewWarehouseList}
                                        tabIndex={!canViewWarehouseList ? -1 : undefined}
                                        onClick={(event) => {
                                            if (!canViewWarehouseList) {
                                                event.preventDefault();
                                                return;
                                            }
                                            navigate(`/warehouses/branch/${branchId}`);
                                        }}
                                    >
                                        <i
                                            className="bi bi-list-ul me-2 d-inline-flex align-items-center lh-1"
                                            style={{ transform: "translateY(1px)" }}
                                            aria-hidden="true"
                                        />
                                        <span>{t("To the warehouse list")}</span>
                                    </Button>
                                    <div className="d-flex flex-column align-items-center gap-3">
                                        {canStartWarehouseTutorial && (
                                            <Button
                                                type="button"
                                                variant="link"
                                                className="p-0 border-0 text-grey text-decoration-underline d-inline-flex align-items-center gap-2 fs-7 fw-normal"
                                                onClick={() => setShowStartWarehouseTutorialModal(true)}
                                                disabled={!canStartWarehouseTutorial}
                                            >
                                                <i className="bi bi-question-circle" />
                                                {t("How to work with warehouses?")}
                                            </Button>
                                        )}
                                        <Button
                                            type="button"
                                            variant="link"
                                            className="p-0 border-0 text-grey text-decoration-underline d-inline-flex align-items-center gap-2 fs-7 fw-normal"
                                            disabled={!canManageModules}
                                            onClick={onToggleWarehouseHandler}
                                        >
                                            <i className="bi bi-power" />
                                            {t("Disable module")}
                                        </Button>
                                    </div>
                                </>
                            )}
                            <p className={`fs-7 text-grey mb-0 ${WAREHOUSES_MODULE.active ? "mt-4" : "mt-3"}`}>
                                {t("You can disable or enable the module’s functionality at any time.")}
                            </p>
                        </div>
                    </Col>
                </Row>
            </div>
            {showToast && <ToastComponent show={showToast} setShow={setShowToast} text={t("Saved")} />}
            {showStartWarehouseTutorialModal && (
                <StartWarehouseTutorialModal
                    show={showStartWarehouseTutorialModal}
                    handleClose={() => setShowStartWarehouseTutorialModal(false)}
                />
            )}
        </div>
    );
};

export default WarehousesModuleDetailsPage;
